import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Pagination,
  SearchInput,
  Spinner,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { SearchIcon, TachometerAltIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type {
  VerdaInstanceTypesState,
  VerdaLocationsState,
  VerdaProductLinksState,
} from '../../../hooks/useAdminVerda';
import { useAdminVerdaAvailability } from '../../../hooks/useAdminVerda';
import { formatCurrencyAmount } from '../../../utils/money';
import { VerdaErrorAlert, VerdaLastFetched, VerdaRefreshButton } from './VerdaTabParts';
import { VerdaInstanceKind, gpuLabel, isConfidential } from './verdaInstanceTypes';
import { VerdaAvailabilityCell, buildAvailabilityRows, filterAvailabilityRows } from './verdaAvailability';

interface VerdaAvailabilityTabProps {
  /** Defers the fetch until the staff gate has passed (the page already renders <NotFound /> otherwise). */
  isStaff: boolean;
  /** The page's shared catalog read, joined for the context columns and the row order. */
  instanceTypes: VerdaInstanceTypesState;
  /** The page's shared locations read, used only for the column headers' names (optional context). */
  locations: VerdaLocationsState;
  /** The page's shared catalog-product links, to show which types are already sellable (optional context). */
  productLinks: VerdaProductLinksState;
}

const renderCell = (cell: VerdaAvailabilityCell) => {
  if (!cell.onDemand && !cell.spot) {
    return '—';
  }
  return (
    <>
      {cell.onDemand && (
        <Label isCompact color="green" className="pf-v6-u-mr-xs">
          On-demand
        </Label>
      )}
      {cell.spot && (
        <Label isCompact color="blue">
          Spot
        </Label>
      )}
    </>
  );
};

/**
 * Admin › Verda › Availability — a live capacity snapshot as a matrix: one row per instance type (in the
 * catalog's order, then codes the catalog doesn't know), one column per location (in the response's
 * order). The snapshot changes minute to minute, but each read costs two Verda calls (the route allows
 * 30/min), so it is read once on open and on Refresh only — never add auto-refresh or polling here; the catalog and location names come from the page's shared reads, which Refresh only
 * retries when they failed. Client-side search/kind filters as on Instance types, plus "Only available".
 */
const VerdaAvailabilityTab: React.FunctionComponent<VerdaAvailabilityTabProps> = ({
  isStaff,
  instanceTypes: catalog,
  locations: locationsState,
  productLinks,
}) => {
  const { availability, fetchedAt, loading, error, refetch } = useAdminVerdaAvailability(isStaff);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kind, setKind] = useState<VerdaInstanceKind>('all');
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Wait for the catalog before building rows (it fixes their order) unless it failed: then fall back
  // to the bare codes from the snapshot.
  const catalogPending = catalog.instanceTypes === null && catalog.error === null;
  const rows = useMemo(
    () => (availability ? buildAvailabilityRows(availability, catalog.instanceTypes) : []),
    [availability, catalog.instanceTypes],
  );
  const filtered = useMemo(
    () => filterAvailabilityRows(rows, debouncedSearch, kind, onlyAvailable),
    [rows, debouncedSearch, kind, onlyAvailable],
  );
  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  const productKeyByCode = useMemo(
    () =>
      new Map(
        (productLinks.links ?? []).flatMap((link) =>
          link.product !== null ? [[link.instance_type, link.product.product_key] as const] : [],
        ),
      ),
    [productLinks.links],
  );

  const locationName = (code: string): string | null =>
    locationsState.locations?.find((location) => location.code === code)?.name ?? null;

  const refresh = async () => {
    await Promise.all([
      refetch(),
      catalog.error !== null ? catalog.refetch() : null,
      locationsState.error !== null ? locationsState.refetch() : null,
    ]);
  };

  const selectKind = (next: VerdaInstanceKind) => {
    setKind(next);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setKind('all');
    setOnlyAvailable(false);
    setPage(1);
  };

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const formatPrice = (price: string | undefined) =>
    price !== undefined && catalog.currency !== null ? formatCurrencyAmount(price, catalog.currency) : '—';

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="availability" message={error} />;
    }
    if (!availability || catalogPending) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the Verda availability" />
        </Bullseye>
      );
    }
    if (availability.length === 0) {
      return (
        <EmptyState titleText="No availability data" icon={TachometerAltIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Verda returned no locations in its availability snapshot.</EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <>
        {catalog.error !== null && (
          <Alert
            variant="warning"
            isInline
            title="Instance type details could not be loaded, so only the codes are shown"
            className="pf-v6-u-mb-md"
          >
            {catalog.error}
          </Alert>
        )}
        {filtered.length === 0 ? (
          <EmptyState titleText="No instance types match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              Try a different search term or kind, or clear the filters to see every instance type, including the ones
              available nowhere right now.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={clearFilters}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <>
            <Table aria-label="Verda instance availability per location" variant="compact">
              <Thead>
                <Tr>
                  <Th>Instance type</Th>
                  <Th>GPU</Th>
                  <Th>On-demand / h</Th>
                  <Th>Spot / h</Th>
                  {availability.map((location) => {
                    const name = locationName(location.location_code);
                    return (
                      <Th key={location.location_code}>
                        {location.location_code}
                        {name !== null && (
                          <div>
                            <Content component="small">{name}</Content>
                          </div>
                        )}
                        <div>
                          <Content component="small" className="pf-v6-u-font-weight-normal">
                            {location.on_demand.length} on-demand · {location.spot.length} spot
                          </Content>
                        </div>
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {paginated.map((row) => (
                  <Tr key={row.code}>
                    <Td dataLabel="Instance type">
                      <span className="pf-v6-u-font-family-monospace">{row.code}</span>
                      {row.type !== null && isConfidential(row.type) && (
                        <Label isCompact color="purple" className="pf-v6-u-ml-sm">
                          Confidential
                        </Label>
                      )}
                      {productKeyByCode.has(row.code) && (
                        <div>
                          <Content component="small">
                            Product{' '}
                            <span className="pf-v6-u-font-family-monospace">{productKeyByCode.get(row.code)}</span>
                          </Content>
                        </div>
                      )}
                    </Td>
                    <Td dataLabel="GPU">{row.type !== null ? gpuLabel(row.type) : '—'}</Td>
                    <Td dataLabel="On-demand / h" modifier="nowrap">
                      {formatPrice(row.type?.prices.on_demand_per_hour)}
                    </Td>
                    <Td dataLabel="Spot / h" modifier="nowrap">
                      {formatPrice(row.type?.prices.spot_per_hour)}
                    </Td>
                    {row.cells.map((cell, index) => (
                      <Td key={availability[index].location_code} dataLabel={availability[index].location_code}>
                        {renderCell(cell)}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Pagination
              itemCount={filtered.length}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
              variant="bottom"
            />
          </>
        )}
      </>
    );
  };

  const hasRows = !error && availability !== null && availability.length > 0 && !catalogPending && rows.length > 0;

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          {hasRows && (
            <>
              <ToolbarItem>
                <SearchInput
                  placeholder="Search by instance type, name or model..."
                  value={searchValue}
                  onChange={(_event, value) => setSearchValue(value)}
                  onClear={() => setSearchValue('')}
                />
              </ToolbarItem>
              <ToolbarItem>
                <ToggleGroup aria-label="Filter by kind">
                  <ToggleGroupItem text="All" isSelected={kind === 'all'} onChange={() => selectKind('all')} />
                  <ToggleGroupItem text="GPU" isSelected={kind === 'gpu'} onChange={() => selectKind('gpu')} />
                  <ToggleGroupItem text="CPU" isSelected={kind === 'cpu'} onChange={() => selectKind('cpu')} />
                </ToggleGroup>
              </ToolbarItem>
              <ToolbarItem alignSelf="center">
                <Switch
                  id="verda-availability-only-available"
                  label="Only available"
                  isChecked={onlyAvailable}
                  onChange={(_event, checked) => {
                    setOnlyAvailable(checked);
                    setPage(1);
                  }}
                />
              </ToolbarItem>
            </>
          )}
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refresh} what="availability" />
          </ToolbarItem>
          {fetchedAt !== null && !error && (
            <ToolbarItem alignSelf="center">
              <VerdaLastFetched fetchedAt={fetchedAt} />
            </ToolbarItem>
          )}
          {hasRows && filtered.length > 0 && (
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filtered.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          )}
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
    </>
  );
};

export default VerdaAvailabilityTab;
